import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { Banner, Button, Field, GoogleMark, Input } from '@acs/ui';
import { PASSWORD_MAX_LENGTH, PASSWORD_MIN_LENGTH } from '@acs/shared';
import { useI18n } from '../../i18n/index.js';
import {
  NeedsLinkError,
  NotAllowedError,
  signInWithEmail,
  signInWithGoogle,
  signUpWithEmail,
} from '../../shared/api/auth.js';
import { authErrorKey } from '../../shared/api/authErrors.js';
import { PageLoader } from '../../shared/ui/PageLoader.js';
import { useAuth } from './AuthProvider.js';
import { AuthShell } from './AuthShell.js';
import { authGate, GATE_PATH } from './authGate.js';

/**
 * Email/password sign-in ships dark (doc 88 §8): the form appears only in a
 * build with this flag, which is made AFTER the Email/Password provider is
 * enabled in Firebase. Google sign-in is unchanged either way.
 */
const EMAIL_AUTH_ENABLED = import.meta.env.VITE_EMAIL_AUTH_ENABLED === 'true';

type Mode = 'signIn' | 'signUp';

/** Router state other auth screens hand back here. */
interface SignInNotice {
  notice?: 'passwordChanged';
}

/**
 * Sign-in: Google, or — when enabled — email and password, with a create-account
 * mode (doc 88). An account whose email is not yet confirmed lands on
 * `/verify-email`, not in the app: `authGate` decides, here as everywhere.
 */
export function SignInPage() {
  const { t, locale } = useI18n();
  const gate = authGate(useAuth());
  const location = useLocation();
  const notice = (location.state as SignInNotice | null)?.notice;

  const [mode, setMode] = useState<Mode>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldError, setFieldError] = useState<{ password?: string; confirm?: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [linkFor, setLinkFor] = useState<string | null>(null);
  const [busy, setBusy] = useState<'google' | 'email' | null>(null);

  if (gate === 'loading') return <PageLoader />;
  if (gate !== 'signed_out') return <Navigate to={GATE_PATH[gate]} replace />;

  const fail = (e: unknown) => {
    const code = (e as { code?: string }).code;
    // A popup the user simply closed isn't an error worth shouting about.
    if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') return;
    if (e instanceof NeedsLinkError) {
      setLinkFor(e.email ?? '');
      setMode('signIn');
      if (e.email) setEmail(e.email);
      return;
    }
    // The allowlist rejection is ours; Firebase's own failures are mapped to
    // our wording, never shown raw (doc 88 D12).
    setError(
      e instanceof NotAllowedError
        ? t('error.notAllowed', { account: e.account ?? t('error.thisAccount') })
        : t(authErrorKey(e)),
    );
  };

  const onGoogle = async () => {
    setBusy('google');
    setError(null);
    try {
      await signInWithGoogle();
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const onEmail = async (ev: FormEvent) => {
    ev.preventDefault();
    setError(null);
    if (mode === 'signUp') {
      const problems: typeof fieldError = {};
      if (password.length < PASSWORD_MIN_LENGTH) problems.password = t('auth.passwordTooShort', { min: PASSWORD_MIN_LENGTH });
      else if (password.length > PASSWORD_MAX_LENGTH) problems.password = t('auth.passwordTooLong', { max: PASSWORD_MAX_LENGTH });
      if (confirm !== password) problems.confirm = t('auth.passwordMismatch');
      setFieldError(problems);
      if (problems.password || problems.confirm) return;
    }
    setBusy('email');
    try {
      if (mode === 'signUp') await signUpWithEmail(email, password, locale);
      else await signInWithEmail(email, password);
    } catch (e) {
      fail(e);
    } finally {
      setBusy(null);
    }
  };

  const switchMode = (next: Mode) => {
    setMode(next);
    setError(null);
    setFieldError({});
    setConfirm('');
  };

  return (
    <AuthShell title={t(mode === 'signUp' ? 'auth.signUpTitle' : 'auth.title')} lede={t('auth.lede')}>
      {notice === 'passwordChanged' && !error && (
        <div className="auth__banner">
          <Banner tone="success">{t('action.resetDone')}</Banner>
        </div>
      )}
      {linkFor !== null && (
        <div className="auth__banner">
          <Banner tone="info" title={t('auth.linkTitle')}>
            {t('auth.linkBody', { email: linkFor || t('error.thisAccount') })}
          </Banner>
        </div>
      )}
      {error && (
        <div className="auth__banner">
          <Banner tone="danger" title={t('auth.failed')}>
            {error}
          </Banner>
        </div>
      )}

      <Button
        size="lg"
        variant="secondary"
        block
        loading={busy === 'google'}
        disabled={busy === 'email'}
        leadingIcon={<GoogleMark size={20} />}
        onClick={() => void onGoogle()}
      >
        {t('auth.google')}
      </Button>

      {EMAIL_AUTH_ENABLED && (
        <>
          <div className="auth__divider">{t('auth.or')}</div>
          <form className="auth__fields" onSubmit={(ev) => void onEmail(ev)} noValidate>
            <Field label={t('auth.email')}>
              <Input
                type="email"
                autoComplete="email"
                required
                maxLength={254}
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
              />
            </Field>
            <Field
              label={t('auth.password')}
              hint={mode === 'signUp' ? t('auth.passwordHint', { min: PASSWORD_MIN_LENGTH }) : undefined}
              error={fieldError.password}
            >
              <Input
                type="password"
                autoComplete={mode === 'signUp' ? 'new-password' : 'current-password'}
                required
                maxLength={PASSWORD_MAX_LENGTH}
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
              />
            </Field>
            {mode === 'signUp' && (
              <Field label={t('auth.passwordConfirm')} error={fieldError.confirm}>
                <Input
                  type="password"
                  autoComplete="new-password"
                  required
                  maxLength={PASSWORD_MAX_LENGTH}
                  value={confirm}
                  onChange={(ev) => setConfirm(ev.target.value)}
                />
              </Field>
            )}
            <Button type="submit" size="lg" block loading={busy === 'email'} disabled={busy === 'google'}>
              {t(mode === 'signUp' ? 'auth.signUpSubmit' : 'auth.signInSubmit')}
            </Button>
          </form>
          <div className="auth__links">
            {mode === 'signIn' ? (
              <>
                <button type="button" className="auth__link" onClick={() => switchMode('signUp')}>
                  {t('auth.toSignUp')}
                </button>
                <Link className="auth__link" to="/forgot-password" state={{ email }}>
                  {t('auth.forgot')}
                </Link>
              </>
            ) : (
              <button type="button" className="auth__link" onClick={() => switchMode('signIn')}>
                {t('auth.toSignIn')}
              </button>
            )}
          </div>
        </>
      )}

      <p className="auth__note">{t('auth.note')}</p>
    </AuthShell>
  );
}
